import React, { useEffect, useState } from "react";
import { qaList } from "../../../../Api/apiUtils";
import ldQaFormData from "./Calendar/ldqaform.json";
import { stripRichText } from "./Calendar/richTextUtils";
import { Button, DescriptionList, EmptyState, Loader, Table } from "../../../../ui";
import { SegmentedControl, StatTile } from "./ui-pending";
import SideDrawer from "./ui-pending/SideDrawer";
import "./SeeQAScore.scss";

interface SeeLdQAScoreProps {
  teacherId: string;
}

type LdQuestion = {
  id: string;
  title: string;
  max_marks?: number;
  options?: { breakup?: string };
};

type LdSection = {
  section_title: string;
  section_total?: number;
  questions?: LdQuestion[];
};

type LdScoreItem = {
  title: string;
  score: number | string;
  maxScore: number;
  breakup?: string;
};

type LdQaRow = {
  id?: number | string;
  qaScore?: number | string;
  qaPercentage?: number | string;
  auditDate?: string;
  sessionDate?: string;
  qaDetails: Record<string, any>;
};

const MAX_SCORE = ldQaFormData.maxScore || 50;
const MARK_SECTIONS = (ldQaFormData.pages[1]?.sections || []) as LdSection[];

const QUESTION_TO_PAYLOAD: Record<string, { section: string; field: string }> = {
  artium_jacket: { section: "ProfessionalAppearanceAndGrooming", field: "artiumJacket" },
  overall_professional_presentation: {
    section: "ProfessionalAppearanceAndGrooming",
    field: "overallProfessionalPresentation",
  },
  video_camera_framing: {
    section: "CameraFramingAndVisualSetup",
    field: "videoOnCameraAngleFramingVisibility",
  },
  lighting_background: {
    section: "CameraFramingAndVisualSetup",
    field: "lightingAndBackgroundEnvironment",
  },
  internet_stability: { section: "InternetAndTechnicalStability", field: "internetStability" },
  joining_on_time: { section: "ClassDurationAndPunctuality", field: "joiningOnTime" },
  class_duration: { section: "ClassDurationAndPunctuality", field: "classDuration" },
  no_multitasking_distracted: {
    section: "InterruptionsAndBreaks",
    field: "noMultitaskingUsageOfPhoneTalksEatingDistracted",
  },
  no_breaks_interruptions: { section: "InterruptionsAndBreaks", field: "noBreaksInterruptions" },
  respectful_communication: {
    section: "ProfessionalCommunicationAndConduct",
    field: "respectfulPositiveProfessionalCommunication",
  },
  choice_of_language: {
    section: "ProfessionalCommunicationAndConduct",
    field: "choiceOfLanguageWords",
  },
  no_rudeness_frustration: {
    section: "ProfessionalCommunicationAndConduct",
    field: "noRudenessOrFrustration",
  },
  posture_eye_contact: {
    section: "BodyLanguageAndOnCameraPresence",
    field: "postureEyeContactFacialExpressionsPositiveBodyLanguage",
  },
  energy_enthusiasm: { section: "BodyLanguageAndOnCameraPresence", field: "energyAndEnthusiasm" },
  doubt_solving: { section: "Closing", field: "doubtSolving" },
  next_class_booking: { section: "Closing", field: "nextClassBooking" },
};

const parseQaDetails = (raw: any): Record<string, any> => {
  if (!raw) return {};
  if (typeof raw === "object") return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
};

const isLdQaRecord = (details: Record<string, any>) =>
  String(details?.qaType || "").toUpperCase() === "L&D";

const formatDate = (dateString: string | undefined): string => {
  if (!dateString) return "N/A";
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return dateString;
    const day = date.getDate();
    const month = date.toLocaleString("default", { month: "short" });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  } catch {
    return dateString;
  }
};

const getNumericScore = (row: LdQaRow): number => {
  const value = row.qaScore ?? row.qaDetails?.qaScore;
  const n = Number(value);
  return Number.isNaN(n) ? 0 : n;
};

const getScorePercentage = (row: LdQaRow): string => {
  const stored =
    row.qaPercentage ??
    row.qaDetails?.qaPercentage ??
    row.qaDetails?.qaPercent;
  if (stored != null && stored !== "") {
    const n = Number(stored);
    if (!Number.isNaN(n)) return `${Math.round(n)}%`;
  }
  const score = getNumericScore(row);
  if (!MAX_SCORE) return "N/A";
  return `${Math.round((score / MAX_SCORE) * 100)}%`;
};

const getPayloadRoot = (details: Record<string, any>) => details?.payload?.[0] || {};

const getMarkValue = (details: Record<string, any>, questionId: string): string => {
  const mapping = QUESTION_TO_PAYLOAD[questionId];
  if (!mapping) return "";
  const value = getPayloadRoot(details)?.[mapping.section]?.[mapping.field];
  return value === undefined || value === null ? "" : String(value);
};

const getSectionItems = (details: Record<string, any>, section: LdSection): LdScoreItem[] =>
  (section.questions || []).map((question) => ({
    title: question.title,
    score: getMarkValue(details, question.id) || "N/A",
    maxScore: question.max_marks ?? 0,
    breakup: question.options?.breakup,
  }));

type ListTab = "ld" | "old";

const OLD_QA_SECTIONS: Array<{ title: string; path: string; fields: Array<{ key: string; label: string }> }> = [
  {
    title: "Opening",
    path: "Opening",
    fields: [
      { key: "startedOnTime", label: "Started on Time" },
      { key: "internetConnection", label: "Internet Connection" },
      { key: "greetingIntroduction", label: "Greeting / Introduction" },
    ],
  },
  {
    title: "Feedback",
    path: "Feedback",
    fields: [
      {
        key: "constructiveFeedbackWhileDiscussingTheAssessmentAssignmentOfThePreviousClass",
        label: "Assignment of the Previous Class",
      },
      { key: "aOIWithExamples", label: "AOI with Examples" },
      { key: "addressTheStudentByTheirName", label: "Address the Student by Their Name" },
    ],
  },
  {
    title: "Rapport Building",
    path: "RapportBuilding",
    fields: [
      { key: "engagementLevel", label: "Engagement Level" },
      { key: "madeTheStudentFeelComfortable", label: "Made the Student Feel Comfortable" },
    ],
  },
  {
    title: "Communication Skills and Soft Skills",
    path: "communicationSkillsAndSoftskills",
    fields: [
      {
        key: "deadAirFumblingFillersUnnecessaryMuteNeedsToBeAvoided",
        label: "Dead Air / Fumbling / Fillers / Unnecessary Mute",
      },
      { key: "casualApproachToneUsageOfSlangLanguage", label: "Casual Approach / Tone / Slang Language" },
      { key: "acknowledgment", label: "Acknowledgment" },
      { key: "activeListening", label: "Active Listening" },
      { key: "interruption", label: "Interruption" },
      { key: "energyLevel", label: "Energy Level" },
      { key: "rateOfSpeech", label: "Rate of Speech" },
    ],
  },
  {
    title: "Closing",
    path: "Closing",
    fields: [
      { key: "customerDelight", label: "Customer Delight" },
      { key: "DoubtsSolvingQueryResolution", label: "Doubts Solving / Query Resolution" },
      { key: "energeticAndPositiveClosing", label: "Energetic and Positive Closing" },
      { key: "disconnectionAfterTheStudent", label: "Disconnection after the Student" },
    ],
  },
  {
    title: "Tech & Gadgets",
    path: "TechGadgets",
    fields: [
      { key: "mic", label: "Mic" },
      { key: "headphones", label: "Headphones" },
      { key: "jacket", label: "Jacket" },
      {
        key: "videoQualityCamAngleBodyLanguageBackground",
        label: "Video Quality / Cam Angle / Body Language / Background",
      },
      { key: "practiceStudio", label: "Practice Studio" },
      { key: "pleaseSpecifyTheInstrumentUsed", label: "Instrument Used" },
    ],
  },
  {
    title: "Presentation",
    path: "Presentation",
    fields: [
      { key: "appropriatelyDressed", label: "Appropriately Dressed" },
      { key: "videoOn", label: "Video On" },
      {
        key: "rudeSarcasticIntimidatingBehaviourUnprofessionalism",
        label: "Rude / Sarcastic / Unprofessional Behaviour",
      },
    ],
  },
  {
    title: "Time",
    path: "Time",
    fields: [{ key: "sessionAsPerStandardTime", label: "Session as per Standard Time" }],
  },
];

const getOldFieldValue = (details: Record<string, any>, path: string, key: string) => {
  const value = getPayloadRoot(details)?.[path]?.[key];
  return value === undefined || value === null || value === "" ? "N/A" : String(value);
};

const SeeLdQAScore: React.FC<SeeLdQAScoreProps> = ({ teacherId }) => {
  const [ldQaData, setLdQaData] = useState<LdQaRow[]>([]);
  const [oldQaData, setOldQaData] = useState<LdQaRow[]>([]);
  const [listTab, setListTab] = useState<ListTab>("ld");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRow, setSelectedRow] = useState<LdQaRow | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    if (teacherId) {
      void fetchLdQaData();
    }
  }, [teacherId]);

  const fetchLdQaData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await qaList(teacherId);
      const rows = (Array.isArray(response) ? response : []).map((item: any) => ({
        ...item,
        qaDetails: parseQaDetails(item.qaDetails),
      }));
      setLdQaData(rows.filter((item: LdQaRow) => isLdQaRecord(item.qaDetails)));
      setOldQaData(rows.filter((item: LdQaRow) => !isLdQaRecord(item.qaDetails)));
    } catch (err) {
      console.error("Error fetching L&D QA Score data:", err);
      setError("No Data Found.");
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (row: LdQaRow) => {
    setSelectedRow(row);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedRow(null);
  };

  const handleTabChange = (tab: ListTab) => {
    setListTab(tab);
    handleCloseDrawer();
  };


  if (loading) {
    return <Loader label="Loading QA scores…" />;
  }

  if (error) {
    return <EmptyState title={error} description="No L&D QA audits could be loaded for this teacher." />;
  }

  const rows = listTab === "ld" ? ldQaData : oldQaData;

  return (
    <div className="tp-qascore">
      <div className="tp-qascore__header">
        <h2 className="ds-card__title">L&D QA Score</h2>
        <p className="ds-card__subtitle">This list includes the score of all the classes that were audited.</p>
        <SegmentedControl
          label="QA type"
          size="sm"
          value={listTab}
          onChange={(value) => handleTabChange(value as ListTab)}
          options={[
            { value: "ld", label: "L&D QA" },
            { value: "old", label: "Old QA" },
          ]}
        />
      </div>

      {rows.length === 0 ? (
        <EmptyState title="No audit data available at the moment." />
      ) : listTab === "ld" ? (
        <Table caption="L&D QA audits">
          <thead>
            <tr>
              <th>Auditor Name</th>
              <th>Student</th>
              <th>Class No</th>
              <th>Course</th>
              <th>Audited On</th>
              <th className="ds-num">Audit Score %</th>
              <th>Audit Category</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {ldQaData.map((item, index) => {
              const details = item.qaDetails || {};
              const isSelected = selectedRow?.id === item.id;
              return (
                <tr
                  key={item.id || details.classId || index}
                  className={`tp-qascore__row ${isSelected ? "tp-qascore__row--selected" : ""}`}
                  onClick={() => handleViewDetails(item)}
                >
                  <td>{details.auditor || "N/A"}</td>
                  <td>{details.studentName || "N/A"}</td>
                  <td>Class {details.classNumber || "N/A"}</td>
                  <td>{details.course || "N/A"}</td>
                  <td>{formatDate(item.auditDate || details.auditDate)}</td>
                  <td className="ds-num">{getScorePercentage(item)}</td>
                  <td>{details.teacherCategory || details.feedbackStatus || "N/A"}</td>
                  <td>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewDetails(item);
                      }}
                    >
                      View Details
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      ) : (
        <Table caption="Old QA audits">
          <thead>
            <tr>
              <th>Teacher Name</th>
              <th>Teacher Category</th>
              <th>Student Name</th>
              <th>Age Group</th>
              <th>Course</th>
              <th>Session Date</th>
              <th>Audit Date</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {oldQaData.map((item, index) => {
              const details = item.qaDetails || {};
              const isSelected = selectedRow?.id === item.id;
              return (
                <tr
                  key={item.id || index}
                  className={`tp-qascore__row ${isSelected ? "tp-qascore__row--selected" : ""}`}
                  onClick={() => handleViewDetails(item)}
                >
                  <td>{details.teacherName || "N/A"}</td>
                  <td>{details.teacherCategory || "N/A"}</td>
                  <td>{details.studentName || "N/A"}</td>
                  <td>{details.ageGroup || "N/A"}</td>
                  <td>{details.course || "N/A"}</td>
                  <td>{formatDate(item.sessionDate || details.sessionDate)}</td>
                  <td>{formatDate(item.auditDate || details.auditDate)}</td>
                  <td>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewDetails(item);
                      }}
                    >
                      View Details
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}

      {drawerOpen && selectedRow && (
        listTab === "ld" ? (
          <LdQaScoreDetails row={selectedRow} onClose={handleCloseDrawer} />
        ) : (
          <OldQaScoreDetails row={selectedRow} onClose={handleCloseDrawer} />
        )
      )}
    </div>
  );
};

/** One label/score card in the drawer. */
const ScoreItem: React.FC<{ title: React.ReactNode; hint?: React.ReactNode; score?: React.ReactNode }> = ({
  title,
  hint,
  score,
}) => (
  <div className="tp-qascore-detail__item">
    <div className="tp-qascore-detail__item-body">
      <div className="tp-qascore-detail__item-text">{title}</div>
      {hint ? <div className="tp-qascore-detail__item-hint">{hint}</div> : null}
    </div>
    {score !== undefined ? <div className="tp-qascore-detail__item-score">{score}</div> : null}
  </div>
);

const LdQaScoreDetails: React.FC<{ row: LdQaRow; onClose: () => void }> = ({ row, onClose }) => {
  const details = row.qaDetails || {};
  const totalScore = getNumericScore(row);
  const scorePercentage = getScorePercentage(row);

  return (
    <SideDrawer
      label="L&D QA audit details"
      onClose={onClose}
      header={
        <DescriptionList
          items={[
            { label: "Auditor Name", value: details.auditor || "N/A" },
            { label: "Student Name", value: details.studentName || "N/A" },
            { label: "Class Number", value: details.classNumber || "N/A" },
            { label: "Course Name", value: details.course || "N/A" },
            { label: "Audit Date", value: formatDate(row.auditDate || details.auditDate) },
          ]}
        />
      }
    >
      <div className="tp-qascore-detail">
        <div className="tp-qascore-detail__scores">
          <StatTile tone="primary" label="Audit Score %" value={scorePercentage} />
          <StatTile tone="primary" label="L&D QA Score" value={`${totalScore}/${MAX_SCORE}`} />
        </div>

        {MARK_SECTIONS.map((section) => {
          const items = getSectionItems(details, section);
          if (!items.length) return null;
          return (
            <section key={section.section_title} className="tp-qascore-detail__section">
              <h3 className="tp-qascore-detail__section-title">
                {section.section_title}
                {section.section_total != null ? ` (Total ${section.section_total})` : ""}
              </h3>
              {items.map((item) => (
                <ScoreItem
                  key={item.title}
                  title={item.title}
                  hint={item.breakup}
                  score={`${item.score}/${item.maxScore}`}
                />
              ))}
            </section>
          );
        })}
        {details.observation ? (
          <section className="tp-qascore-detail__section">
            <h3 className="tp-qascore-detail__section-title">Comment</h3>
            <div className="tp-qascore-detail__item">
              <div className="tp-qascore-detail__item-body">
                {details.observation.includes("<") ? (
                  <div
                    className="tp-qascore-detail__item-text tp-qascore-detail__rich"
                    dangerouslySetInnerHTML={{ __html: details.observation }}
                  />
                ) : (
                  <div className="tp-qascore-detail__item-text tp-qascore-detail__item-text--pre">
                    {stripRichText(details.observation) || details.observation}
                  </div>
                )}
              </div>
            </div>
          </section>
        ) : null}
      </div>
    </SideDrawer>
  );
};

const OldQaScoreDetails: React.FC<{ row: LdQaRow; onClose: () => void }> = ({ row, onClose }) => {
  const details = row.qaDetails || {};

  return (
    <SideDrawer
      label="Old QA audit details"
      onClose={onClose}
      header={
        <DescriptionList
          items={[
            { label: "Teacher Name", value: details.teacherName || "N/A" },
            { label: "Student Name", value: details.studentName || "N/A" },
            { label: "Course", value: details.course || "N/A" },
            { label: "Teacher Category", value: details.teacherCategory || "N/A" },
            { label: "Session Date", value: formatDate(row.sessionDate || details.sessionDate) },
            { label: "Audit Date", value: formatDate(row.auditDate || details.auditDate) },
          ]}
        />
      }
    >
      <div className="tp-qascore-detail">
        <div className="tp-qascore-detail__scores">
          <StatTile tone="primary" label="QA Score" value={row.qaScore ?? details.qaScore ?? "N/A"} />
        </div>

        {(details.feedbackStatus || details.feedbackLink || details.observation) && (
          <section className="tp-qascore-detail__section">
            <h3 className="tp-qascore-detail__section-title">Basic Information</h3>
            {details.feedbackStatus ? <ScoreItem title={`Feedback Status: ${details.feedbackStatus}`} /> : null}
            {details.feedbackLink ? <ScoreItem title={`Feedback Link: ${details.feedbackLink}`} /> : null}
            {details.observation ? <ScoreItem title={`Observation: ${details.observation}`} /> : null}
          </section>
        )}

        {OLD_QA_SECTIONS.map((section) => (
          <section key={section.title} className="tp-qascore-detail__section">
            <h3 className="tp-qascore-detail__section-title">{section.title}</h3>
            {section.fields.map((field) => (
              <ScoreItem
                key={field.key}
                title={field.label}
                score={getOldFieldValue(details, section.path, field.key)}
              />
            ))}
          </section>
        ))}
      </div>
    </SideDrawer>
  );
};

export default SeeLdQAScore;
